"use client";

import * as React from "react";
import { Autocomplete as AutocompletePrimitive } from "@base-ui/react/autocomplete";
import { SearchIcon } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { cn } from "@workspace/ui/lib/utils";

// HA-810 — the command list on Base UI's Autocomplete (inline, always open), in place of cmdk, with
// cmdk's API kept so its callers don't change: items are declared as children with a `value`,
// optional `keywords` and an `onSelect(value)`, and the list filters itself as you type.
//
// Autocomplete is the combobox that remembers no selection, as cmdk didn't: each caller keeps its
// own value. Base UI filters an `items` array; these callers declare items as children. So each
// item checks itself against the query and renders nothing when it doesn't match, and the list
// tracks which match so `CommandEmpty` knows when to show. Base UI still owns the keyboard: the
// arrow keys, Home/End and Enter move and pick over whatever items are rendered.
//
// Matching is Base UI's `contains`, ignoring case and accents, in the list's own order (Chris,
// HA-810 B1). cmdk matched fuzzily and re-ranked by score; neither is kept.

interface CommandStore {
	matches: (query: string, value: string, keywords?: string[]) => boolean;
	getQuery: () => string;
	setQuery: (query: string) => void;
	register: (id: string, visible: boolean) => void;
	unregister: (id: string) => void;
	subscribe: (listener: () => void) => () => void;
	visibleCount: () => number;
}

const CommandContext = React.createContext<CommandStore | null>(null);

function useCommandStore(part: string): CommandStore {
	const store = React.use(CommandContext);
	if (!store) throw new Error(`${part} must be used within <Command>`);
	return store;
}

// The search and which items match live outside React state, so a caller-controlled input (an
// effect) and the items (effects) can update them, and the parts that care subscribe.
function createVisibility() {
	const visible = new Map<string, boolean>();
	const listeners = new Set<() => void>();
	let count = 0;
	let query = "";
	const notify = () => {
		for (const listener of listeners) listener();
	};
	const recount = () => {
		const next = [...visible.values()].filter(Boolean).length;
		if (next === count) return;
		count = next;
		notify();
	};
	return {
		getQuery: () => query,
		setQuery(next: string) {
			if (next === query) return;
			query = next;
			notify();
		},
		register(id: string, isVisible: boolean) {
			visible.set(id, isVisible);
			recount();
		},
		unregister(id: string) {
			visible.delete(id);
			recount();
		},
		subscribe(listener: () => void) {
			listeners.add(listener);
			return () => {
				listeners.delete(listener);
			};
		},
		visibleCount: () => count,
	};
}

function Command({
	className,
	children,
	filter: customFilter,
	value: _value,
	...props
}: React.ComponentProps<"div"> & {
	/**
	 * cmdk's custom filter, kept for its callers: a score above 0 is a match. Without it, an item
	 * matches when its value or a keyword contains the query, ignoring case and accents (B1).
	 */
	filter?: (value: string, search: string, keywords?: string[]) => number;
	/**
	 * cmdk's controlled highlighted item. Accepted so callers keep compiling, but unused: the top
	 * match is highlighted instead (autoHighlight, B2).
	 */
	value?: string;
}) {
	const [visibility] = React.useState(createVisibility);
	const query = React.useSyncExternalStore(
		visibility.subscribe,
		visibility.getQuery,
		visibility.getQuery,
	);
	const filter = AutocompletePrimitive.useFilter();

	const store = React.useMemo<CommandStore>(
		() => ({
			matches: (query, value, keywords) =>
				customFilter
					? query.trim() === "" || customFilter(value, query, keywords) > 0
					: query.trim() === "" ||
						[value, ...(keywords ?? [])].some((text) =>
							filter.contains(text, query),
						),
			...visibility,
		}),
		[filter, customFilter, visibility],
	);

	return (
		<CommandContext value={store}>
			<AutocompletePrimitive.Root
				inline
				// Items are static here: each filters itself, and the input isn't rewritten from the
				// highlighted item.
				mode="none"
				// B2: the first matching item is always highlighted, so Enter picks the top match.
				autoHighlight="always"
				// B3: the arrow keys stop at the ends, as cmdk's did.
				loopFocus={false}
				value={query}
				onValueChange={(next, details) => {
					// Only typing moves the search; a pick leaves it as it was, as cmdk did.
					if (details.reason !== "item-press") visibility.setQuery(next);
				}}
			>
				<div
					data-slot="command"
					className={cn(
						"bg-popover text-popover-foreground flex h-full w-full flex-col overflow-hidden rounded-md",
						className,
					)}
					{...props}
				>
					{children}
				</div>
			</AutocompletePrimitive.Root>
		</CommandContext>
	);
}

function CommandDialog({
	title = "Command Palette",
	description = "Search for a command to run...",
	children,
	className,
	showCloseButton = true,
	...props
}: Omit<React.ComponentProps<typeof Dialog>, "children"> & {
	children?: React.ReactNode;
	title?: string;
	description?: string;
	className?: string;
	showCloseButton?: boolean;
}) {
	return (
		<Dialog {...props}>
			<DialogHeader className="sr-only">
				<DialogTitle>{title}</DialogTitle>
				<DialogDescription>{description}</DialogDescription>
			</DialogHeader>
			<DialogContent
				className={cn("overflow-hidden p-0", className)}
				showCloseButton={showCloseButton}
			>
				<Command className="**:data-[slot=command-group-heading]:text-muted-foreground **:data-[slot=command-input-wrapper]:h-12 **:data-[slot=command-group-heading]:px-2 **:data-[slot=command-group-heading]:font-medium **:data-[slot=command-group]:px-2 **:data-[slot=command-input]:h-12 **:data-[slot=command-item]:px-2 **:data-[slot=command-item]:py-3">
					{children}
				</Command>
			</DialogContent>
		</Dialog>
	);
}

function CommandInput({
	className,
	value,
	onValueChange,
	...props
}: Omit<React.ComponentProps<"input">, "value" | "onChange"> & {
	value?: string;
	onValueChange?: (search: string) => void;
}) {
	const store = useCommandStore("CommandInput");
	// A caller that controls the search (e.g. to clear it after a pick) drives the list's query.
	React.useEffect(() => {
		if (value !== undefined) store.setQuery(value);
	}, [store, value]);

	return (
		<div
			data-slot="command-input-wrapper"
			className="flex h-9 items-center gap-2 border-b px-3"
		>
			<SearchIcon className="size-4 shrink-0 opacity-50" />
			<AutocompletePrimitive.Input
				data-slot="command-input"
				className={cn(
					"placeholder:text-muted-foreground flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-hidden disabled:cursor-not-allowed disabled:opacity-50",
					className,
				)}
				onChange={(event) => onValueChange?.(event.currentTarget.value)}
				{...props}
				onKeyDown={(event) => {
					props.onKeyDown?.(event);
					if (event.key !== "Enter" || event.defaultPrevented) return;
					// Enter picks the highlighted item, as cmdk's did; Base UI's static list doesn't do
					// it on its own. It also keeps Enter from submitting a form the list sits in.
					const id = event.currentTarget.getAttribute("aria-activedescendant");
					const item = id
						? event.currentTarget.ownerDocument.getElementById(id)
						: null;
					if (item) {
						event.preventDefault();
						item.click();
					}
				}}
			/>
		</div>
	);
}

function CommandList({ className, ...props }: React.ComponentProps<"div">) {
	return (
		<AutocompletePrimitive.List
			data-slot="command-list"
			className={cn(
				"max-h-[300px] scroll-py-1 overflow-x-hidden overflow-y-auto",
				className,
			)}
			{...props}
		/>
	);
}

function CommandEmpty({ className, ...props }: React.ComponentProps<"div">) {
	const store = useCommandStore("CommandEmpty");
	const visibleCount = React.useSyncExternalStore(
		store.subscribe,
		store.visibleCount,
		store.visibleCount,
	);

	if (visibleCount > 0) return null;

	return (
		<div
			data-slot="command-empty"
			role="presentation"
			className={cn("py-6 text-center text-sm", className)}
			{...props}
		/>
	);
}

function CommandGroup({
	className,
	heading,
	children,
	...props
}: React.ComponentProps<"div"> & { heading?: React.ReactNode }) {
	return (
		<AutocompletePrimitive.Group
			data-slot="command-group"
			className={cn("text-foreground overflow-hidden p-1", className)}
			{...props}
		>
			{heading !== undefined && (
				<AutocompletePrimitive.GroupLabel
					data-slot="command-group-heading"
					className="text-muted-foreground px-2 py-1.5 text-xs font-medium"
				>
					{heading}
				</AutocompletePrimitive.GroupLabel>
			)}
			{children}
		</AutocompletePrimitive.Group>
	);
}

function CommandSeparator({
	className,
	...props
}: React.ComponentProps<"div">) {
	return (
		<AutocompletePrimitive.Separator
			data-slot="command-separator"
			className={cn("bg-border -mx-1 h-px", className)}
			{...props}
		/>
	);
}

function CommandItem({
	className,
	value,
	keywords,
	onSelect,
	disabled,
	children,
	...props
}: Omit<React.ComponentProps<"div">, "onSelect"> & {
	/** What the item is matched on and passed to `onSelect`; defaults to its text. */
	value?: string;
	/** Further words it matches on, e.g. a currency's code as well as its name. */
	keywords?: string[];
	onSelect?: (value: string) => void;
	disabled?: boolean;
}) {
	const store = useCommandStore("CommandItem");
	const id = React.useId();
	const query = React.useSyncExternalStore(
		store.subscribe,
		store.getQuery,
		store.getQuery,
	);
	const itemValue = value ?? textOf(children);
	const visible = store.matches(query, itemValue, keywords);

	React.useEffect(() => {
		store.register(id, visible);
	}, [store, id, visible]);
	React.useEffect(() => () => store.unregister(id), [store, id]);

	if (!visible) return null;

	return (
		<AutocompletePrimitive.Item
			data-slot="command-item"
			value={itemValue}
			disabled={disabled}
			// A click, or Enter on the highlighted item, which Base UI turns into a click.
			onClick={() => {
				if (!disabled) onSelect?.(itemValue);
			}}
			className={cn(
				"data-highlighted:bg-accent data-highlighted:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
				className,
			)}
			{...props}
		>
			{children}
		</AutocompletePrimitive.Item>
	);
}

/** An item's text, for one declared without a `value` (cmdk fell back to it the same way). */
function textOf(node: React.ReactNode): string {
	if (node == null || typeof node === "boolean") return "";
	if (typeof node === "string" || typeof node === "number") return String(node);
	if (Array.isArray(node)) return node.map(textOf).join("");
	if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
		return textOf(node.props.children);
	}
	return "";
}

function CommandShortcut({
	className,
	...props
}: React.ComponentProps<"span">) {
	return (
		<span
			data-slot="command-shortcut"
			className={cn(
				"text-muted-foreground ml-auto text-xs tracking-widest",
				className,
			)}
			{...props}
		/>
	);
}

export {
	Command,
	CommandDialog,
	CommandInput,
	CommandList,
	CommandEmpty,
	CommandGroup,
	CommandItem,
	CommandShortcut,
	CommandSeparator,
};
